/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable, inject } from "@angular/core";
import { DocumentsPlusScanning } from "@app/app-modules/documents-plus/documents-plus-scanning/documents-plus-scanning.model";
import {
  DocumentsPlusDocumentRequestAdapter,
  DocumentsPlusDocumentResponseAdapter
} from "@app/app-modules/documents-plus/documents-plus-upload/documents-plus-upload.model";
import {
  DocumentWizard,
  DocumentWizardDocument,
  DocumentsPlusDocWizAdapter,
  DocumentsPlusDocWizListAdapter
} from "@app/app-modules/documents-plus/documents-plus-wizard-list/documents-plus-wizard.model";
import { DocumentsPlus, DocumentsPlusView, DocumentsPlusViewDocumentAdapter } from "@app/app-modules/documents-plus/documents-plus.model";
import {
  DocumentsPlusFileRoomAdapter,
  DocumentsPlusFileCabinetAdapter,
  DocumentsPlusFoldersAdapter,
  DocumentsPlusSubFoldersAdapter,
  DocumentsPlusFilesAdapter
} from "@app/app-modules/documents-plus/documents-plus/documents-plus-files.model";
import { FileDownloadService } from "@app/core/services/file-download.service";
import { HttpService } from "@app/core/services/http/http.service";
import { LoaderService } from "@app/core/services/loader.service";
import { ModalSheetService } from "@app/core/services/modal-sheet.service";
import { PdfDocument } from "@app/shared/components/pdf-viewer/pdf-viewer-model";
import { PdfViewerService } from "@app/shared/components/pdf-viewer/pdf-viewer.service";
import { FileTypes } from "@app/shared/constants/file-types.constants";
import { DPLUS_URL } from "@app/shared/constants/url.constants";
import { FileDownload } from "@app/shared/models/file.model";
import { PdfMessage } from "@app/shared/models/message.model";
import { map } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class DocumentsPlusService {
  private http = inject(HttpService);
  private fds = inject(FileDownloadService);
  private pdfService = inject(PdfViewerService);
  private loaderService = inject(LoaderService);
  private modalSheetService = inject(ModalSheetService);

  //////////////////////
  // File/Folder System
  //////////////////////

  fetchFileRooms(appId: string) {
    const adapter = new DocumentsPlusFileRoomAdapter();
    return this.http
      .addDataParams({ applicationId: appId })
      .get<DocumentsPlus[]>(DPLUS_URL.DOCUMENTSPLUS_FILE_ROOMS)
      .pipe(map((response: any) => adapter.adapt(response)));
  }

  fetchFileCabinets(appId: string, _cabinetId: string) {
    const adapter = new DocumentsPlusFileCabinetAdapter();
    return this.http
      .addDataParams({ applicationId: appId })
      .get<DocumentsPlus[]>(DPLUS_URL.DOCUMENTSPLUS_FILE_CABINETS)
      .pipe(map((response: any) => adapter.adapt(response)));
  }

  fetchFolders(appId: string, _cabinetId: string) {
    const adapter = new DocumentsPlusFoldersAdapter();
    return this.http
      .addDataParams({ applicationId: appId })
      .get<DocumentsPlus[]>(DPLUS_URL.DOCUMENTSPLUS_FOLDERS)
      .pipe(map((response: any) => adapter.adapt(response)));
  }

  fetchSubFolders(appId: string, _cabinetId: string) {
    const adapter = new DocumentsPlusSubFoldersAdapter();
    return this.http
      .addDataParams({ applicationId: appId })
      .get<DocumentsPlus[]>(DPLUS_URL.DOCUMENTSPLUS_SUBFOLDERS)
      .pipe(map((response: any) => adapter.adapt(response)));
  }

  fetchFiles(appId: string, _cabinetId: string) {
    const adapter = new DocumentsPlusFilesAdapter();
    return this.http
      .addDataParams({ applicationId: appId })
      .get<DocumentsPlus[]>(DPLUS_URL.DOCUMENTSPLUS_FILES)
      .pipe(map((response: any) => adapter.adapt(response)));
  }

  ////////////
  // COMMON FUNCTIONS
  ////////////

  /**
   * View In Basket document in PDF viewer
   * @param parms
   * @returns
   */
  viewDocument(parms: DocumentsPlusView) {
    const adapter = new DocumentsPlusViewDocumentAdapter();
    return this.http
      .addDataParams(parms)
      .get<DocumentsPlusView>(DPLUS_URL.INBASKET_VIEW)
      .pipe(map((response: any) => adapter.adapt(response)));
  }

  /**
   * Add new document documentsPlus
   * @param parms
   * @returns
   */
  addDocument(parms: DocumentsPlusScanning, skipLoader?: boolean) {
    const reqAdapter = new DocumentsPlusDocumentRequestAdapter();
    const respAdapter = new DocumentsPlusDocumentResponseAdapter();
    return this.http
      .skipLoaderHandler(skipLoader)
      .addDataParams(reqAdapter.adapt(parms))
      .post<DocumentsPlusScanning>(DPLUS_URL.INBASKET_SCANNING, "")
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  /**
   * Fetch Document Wizard documents list
   * @param documentId
   * @returns
   */
  fetchDocumentWizardList(parms: DocumentWizard) {
    const respAdapter = new DocumentsPlusDocWizListAdapter();
    return this.http
      .addDataParams(parms)
      .get<DocumentWizard>(DPLUS_URL.DOCUMENT_WIZARD_LIST)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }
  /**
   * Fetch Document Wizard document
   * @param documentId
   * @returns
   */
  fetchDocumentWizardById(documentId: string, skipLoader?: boolean) {
    const respAdapter = new DocumentsPlusDocWizAdapter();
    return this.http
      .skipLoaderHandler(skipLoader)
      .addDataParams({ documentId: documentId })
      .get<DocumentWizardDocument>(DPLUS_URL.DOCUMENT_WIZARD_INDEXED_DOCUMENT)
      .pipe(map((response: any) => respAdapter.adapt(response)));
  }

  /**
   * Handle fetching the document wizard document and viewing it
   * @param file
   * @param additionalDocs
   * @param addAdditional : append to pdf viewer doc list only
   * @param findNextPdf : if the file parm is not a PDF, go to the next file
   * @param applicationId
   * @param fnc : Method to use instead of the open pdf call
   * @returns
   */
  fetchAndViewDocument(file: PdfDocument, additionalDocs?: PdfDocument[], findNextPdf: boolean = true, applicationId?: string, fnc?: Function) {
    if (!file?.docWiz) {
      return;
    }

    // Get document
    this.fetchDocumentWizardById(file.docWiz.document)
      .subscribe({
        next: (resp) => {
          // Sync path in docs list to show correct active document
          const parmIndex = additionalDocs?.findIndex((x) => x?.docWiz?.document === file.docPath);
          if (parmIndex > -1) {
            additionalDocs[parmIndex].docPath = resp.file;
          }

          file.docPath = resp.file;
          this.handleViewDocument(file, additionalDocs, findNextPdf, applicationId, fnc);
        },
        error: () => {}
      })
      .add(() => {
        this.modalSheetService.closeAll();
        this.loaderService.enableLoader();
      });
  }

  /**
   * Handle viewing an indexed document based on type.
   * @param file
   * @param additionalDocs
   * @param findNextPdf : if the file parm is not a PDF, go to the next file
   * @param applicationId
   * @param fnc : Method to use instead of the open pdf call
   * @returns
   */
  handleViewDocument(file: PdfDocument, additionalDocs?: PdfDocument[], findNextPdf?: boolean, applicationId?: string, fnc?: Function) {
    switch (this.getFileType(file)) {
      case FileTypes.ADOBE: {
        const data: PdfMessage = {
          applicationId: applicationId,
          document: { ...file },
          documentList: additionalDocs
        };

        if (fnc) {
          fnc(data.document.docPath);
        } else {
          this.pdfService.openPdf(data);
        }
        break;
      }
      default: {
        const parms: FileDownload = {
          file: file.docPath,
          type: FileTypes.EMPTY
        };
        this.fds.downloadAndSave(parms);

        // Find next available record in the array and re-run the process until a PDF or undefined is found.
        if (additionalDocs?.length && findNextPdf) {
          const i = additionalDocs.findIndex((x) => x.docPath === file.docPath);
          if (i > -1) {
            additionalDocs.splice(i, 1);
            let docToUse = additionalDocs[i];

            // If the document that is removed is the last in the array and there are more documents available
            // AND we are calling an outside function to process the document set to the first one.
            // NOTE: This typically ONLY happens if a non-PDF file is sent to the PDF-viewer
            // from document wizard.
            if (additionalDocs[i] === undefined && additionalDocs?.length && fnc) {
              docToUse = additionalDocs[0];
            }

            this.fetchAndViewDocument(docToUse, additionalDocs, findNextPdf, applicationId, fnc);
          }
        }
      }
    }
  }

  getFileType(file: PdfDocument) {
    return this.fds.getDocumentType(file?.docWiz?.extension?.trim() ?? file.docPath)?.toLowerCase();
  }
}
